/*
 * Juo Subscriptions – custom dropdown voor de abonnementskeuze.
 *
 * Vervangt de OS-native optielijst door een eigen listbox in huisstijl. Het native <select> blijft
 * (onzichtbaar) de bron van waarheid: een keuze zet select.value en stuurt een 'change'-event, waar
 * Juo op reageert (selling_plan-veld, prijs en modus). Wijzigingen door Juo zelf (bijv. andere variant
 * of nieuwe opties) worden teruggesynchroniseerd. Styling staat in juo-custom.css.
 */

const SELECT_SELECTOR = '#juo-subscription-widget .j-select-wrapper select';
let uid = 0;

class JuoDropdown {
  constructor(select) {
    this.select = select;
    this.wrapper = select.closest('.j-select-wrapper');
    this.id = `juo-dd-${++uid}`;
    this.activeIndex = -1;

    this.button = document.createElement('button');
    this.button.type = 'button';
    this.button.className = 'juo-dd__button';
    this.button.setAttribute('aria-haspopup', 'listbox');
    this.button.setAttribute('aria-expanded', 'false');
    this.button.setAttribute('aria-controls', `${this.id}-list`);
    this.label = document.createElement('span');
    this.label.className = 'juo-dd__label';
    this.button.append(this.label);

    this.list = document.createElement('ul');
    this.list.className = 'juo-dd__list';
    this.list.id = `${this.id}-list`;
    this.list.setAttribute('role', 'listbox');
    this.list.tabIndex = -1;
    this.list.hidden = true;

    // Native select blijft in het formulier, maar niet meer bereikbaar voor muis/toetsenbord/screenreader
    select.tabIndex = -1;
    select.setAttribute('aria-hidden', 'true');
    this.wrapper.classList.add('juo-dd');
    this.wrapper.append(this.button, this.list);

    this.button.addEventListener('click', () => this.toggle());
    this.button.addEventListener('keydown', (event) => this.onButtonKeydown(event));
    this.list.addEventListener('keydown', (event) => this.onListKeydown(event));
    this.list.addEventListener('click', (event) => {
      const option = event.target.closest('[role="option"]');
      if (option) this.choose(Number(option.dataset.index));
    });
    this.list.addEventListener('mousemove', (event) => {
      const option = event.target.closest('[role="option"]');
      if (option) this.setActive(Number(option.dataset.index), false);
    });
    this.onDocumentPointerDown = (event) => {
      if (!this.wrapper.contains(event.target)) this.close(false);
    };

    select.addEventListener('change', () => this.syncSelection());
    this.observer = new MutationObserver(() => this.render());
    this.observer.observe(select, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['disabled', 'hidden'] });

    this.render();
  }

  get options() {
    return [...this.select.options];
  }

  render() {
    this.list.replaceChildren(
      ...this.options.map((option, index) => {
        const item = document.createElement('li');
        item.id = `${this.id}-option-${index}`;
        item.className = 'juo-dd__option';
        item.setAttribute('role', 'option');
        item.dataset.index = index;
        item.textContent = option.text.trim();
        item.hidden = option.hidden;
        if (option.disabled) item.setAttribute('aria-disabled', 'true');
        return item;
      })
    );
    this.syncSelection();
  }

  syncSelection() {
    const selectedIndex = this.select.selectedIndex;
    this.label.textContent = this.options[selectedIndex]?.text.trim() ?? '';
    this.button.disabled = this.select.disabled;
    [...this.list.children].forEach((item, index) => item.setAttribute('aria-selected', String(index === selectedIndex)));
  }

  isSelectable(index) {
    const option = this.options[index];
    return option && !option.disabled && !option.hidden;
  }

  step(from, direction) {
    for (let index = from + direction; index >= 0 && index < this.options.length; index += direction) {
      if (this.isSelectable(index)) return index;
    }
    return from;
  }

  setActive(index, scroll = true) {
    if (!this.isSelectable(index)) return;
    this.list.querySelector('.is-active')?.classList.remove('is-active');
    const item = this.list.children[index];
    item.classList.add('is-active');
    this.list.setAttribute('aria-activedescendant', item.id);
    this.activeIndex = index;
    if (scroll) item.scrollIntoView({ block: 'nearest' });
  }

  open() {
    if (!this.list.hidden || this.button.disabled) return;
    this.list.hidden = false;
    this.wrapper.classList.add('is-open');
    this.button.setAttribute('aria-expanded', 'true');

    // Openklappen naar boven als er onder te weinig ruimte is
    const buttonRect = this.button.getBoundingClientRect();
    this.wrapper.classList.toggle('juo-dd--up', window.innerHeight - buttonRect.bottom < this.list.offsetHeight + 16 && buttonRect.top > this.list.offsetHeight + 16);

    this.setActive(this.isSelectable(this.select.selectedIndex) ? this.select.selectedIndex : this.step(-1, 1));
    this.list.focus({ preventScroll: true });
    document.addEventListener('pointerdown', this.onDocumentPointerDown);
  }

  close(returnFocus = true) {
    if (this.list.hidden) return;
    this.list.hidden = true;
    this.wrapper.classList.remove('is-open', 'juo-dd--up');
    this.button.setAttribute('aria-expanded', 'false');
    this.list.removeAttribute('aria-activedescendant');
    document.removeEventListener('pointerdown', this.onDocumentPointerDown);
    if (returnFocus) this.button.focus();
  }

  toggle() {
    this.list.hidden ? this.open() : this.close();
  }

  choose(index) {
    if (!this.isSelectable(index)) return;
    if (index !== this.select.selectedIndex) {
      this.select.selectedIndex = index;
      this.select.dispatchEvent(new Event('input', { bubbles: true }));
      this.select.dispatchEvent(new Event('change', { bubbles: true }));
    }
    this.syncSelection();
    this.close();
  }

  onButtonKeydown(event) {
    if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
      event.preventDefault();
      this.open();
    }
  }

  onListKeydown(event) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.setActive(this.step(this.activeIndex, 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.setActive(this.step(this.activeIndex, -1));
        break;
      case 'Home':
        event.preventDefault();
        this.setActive(this.step(-1, 1));
        break;
      case 'End':
        event.preventDefault();
        this.setActive(this.step(this.options.length, -1));
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.choose(this.activeIndex);
        break;
      case 'Escape':
        event.preventDefault();
        this.close();
        break;
      case 'Tab':
        this.close(false);
        break;
    }
  }
}

const enhanced = new WeakSet();

function enhanceAll(root = document) {
  root.querySelectorAll(SELECT_SELECTOR).forEach((select) => {
    if (enhanced.has(select)) return;
    enhanced.add(select);
    new JuoDropdown(select);
  });
}

enhanceAll();

// Widget kan later verschijnen of opnieuw gerenderd worden (quick buy, variant-wissel, product-rerender)
new MutationObserver((mutations) => {
  if (mutations.some((mutation) => mutation.addedNodes.length)) enhanceAll();
}).observe(document.body, { childList: true, subtree: true });